import * as React from 'react';
import { FC, useCallback, useState } from 'react';

import { Hero } from '@rbilabs/components-library/build/components/hero';
import { useIntl } from 'react-intl';

import { ISanityBlockContent, ISanityImage } from '@rbi-ctg/menu';
import { useShouldShowHero } from 'components/features/hooks/use-should-show-hero';
import Picture from 'components/picture';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import useDialogModal from 'hooks/use-dialog-modal';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { brand } from 'utils/environment';

import linkSerializers from '../terms/terms-link';
import useTerms from '../terms/terms.hook';

import { getHeroTheme } from './config-theme';
import { HeroSection } from './styles';
import { IHeroProps } from './types';

export type ModalPayloadType = { heading: string; content: ISanityBlockContent[] };

/**
 *
 * HeroContainer is the Sanity Data component that wraps the Component-library Hero component
 *
 */
const HeroContainer: FC<IHeroProps> = ({ hero }) => {
  const { formatMessage } = useIntl();
  const { trackEvent } = useMParticleContext();
  const shouldShowHero = useShouldShowHero();

  const [pendingData, setPendingData] = useState<ModalPayloadType>({ content: [], heading: '' });
  const [TermsModal, openTermsModal] = useDialogModal({
    modalAppearanceEventMessage: 'Terms and Conditions',
  });

  const onShowTermsModal = useCallback(
    data => {
      openTermsModal(data);
      setPendingData(data);
    },
    [openTermsModal]
  );

  // Hero content
  const header = hero?.header?.locale ?? '';
  const subheader = hero?.subheader?.locale ?? '';
  const sanityImage = hero.image?.locale as ISanityImage;
  const imageAlt = (hero.altImageText?.locale ?? '') as string;
  const image = sanityImage && (
    <Picture image={sanityImage} alt={imageAlt} objectFitContain={true} resizeToSmall={true} />
  );

  const reversed = hero?.reversed ?? false;

  // Hero CTAs
  const primaryCtaText = (hero?.primaryCta?.actionText?.locale ?? '') as string;
  const primaryCtaHref = (hero?.primaryCta?.actionUrl?.locale ?? '') as string;
  const complementaryCtaText = (hero?.complementaryCta?.actionText?.locale ?? '') as string;
  const complementaryCtaHref = (hero?.complementaryCta?.actionUrl?.locale ?? '') as string;
  const showComplementaryCta = hero?.showComplementaryCTA ?? true;

  const logPrimaryCtaEvent = () => {
    trackEvent({
      name: CustomEventNames.HERO_PRIMARY_CTA_CLICK,
      type: EventTypes.Navigation,
      attributes: {
        'Hero ID': hero._id,
        'Hero URL': primaryCtaHref,
      },
    });
  };
  const logComplementaryCtaEvent = () => {
    trackEvent({
      name: CustomEventNames.HERO_COMPLEMENTARY_CTA_CLICK,
      type: EventTypes.Navigation,
      attributes: {
        'Hero ID': hero._id,
        'Hero URL': complementaryCtaHref,
      },
    });
  };

  // Terms
  const hasTerms = hero.hasTerms;
  // Fallback to "terms and conditions" localized
  const termsButtonText = (hero.termsButton?.actionText?.locale ??
    formatMessage({ id: 'termsConditions' })) as string;
  const termsButtonUrl = (hero.termsButton?.actionUrl?.locale ?? '') as string;
  const termsLinkHref = hasTerms ? termsButtonUrl : undefined;
  const termsLinkText = hasTerms ? termsButtonText : undefined;
  const termsText = hero.termsText?.localeRaw;

  const colorOption = hero?.colorOption || undefined;
  const backgroundColor = hero?.backgroundColor || false;
  const backgroundSplit = hero?.backgroundSplit || false;

  const onClickTermsLink = useTerms({
    onShowTermsModal,
    termsButtonText,
    termsButtonUrl,
    termsText,
  });

  if (!shouldShowHero) {
    return null;
  }

  const configTheme = getHeroTheme({
    brand: brand(),
    color: colorOption,
    hasBackground: backgroundColor,
    reversedFromSanity: reversed,
  });

  return (
    <HeroSection $reversed={reversed}>
      <Hero
        header={header}
        subheader={subheader}
        image={image}
        primaryCtaText={primaryCtaText}
        primaryCtaHref={primaryCtaHref}
        onClickPrimaryCta={logPrimaryCtaEvent}
        complementaryCtaText={complementaryCtaText}
        complementaryCtaHref={complementaryCtaHref}
        onClickComplementaryCta={logComplementaryCtaEvent}
        // Terms
        onClickTermsLink={onClickTermsLink}
        termsLinkHref={termsLinkHref}
        termsLinkText={termsLinkText}
        showComplementaryCta={showComplementaryCta}
        background={configTheme?.background}
        reversed={configTheme?.reversed}
        backgroundSplit={backgroundSplit}
      />
      <TermsModal
        heading={pendingData.heading}
        body={<SanityBlockRenderer content={pendingData?.content} serializers={linkSerializers} />}
      />
    </HeroSection>
  );
};

export default HeroContainer;
